<template>
  <div class="section-list-wrap" :class="{ 'is-editing': editable }">
    <p v-if="locked" class="lock-banner">
      <span class="lock-banner-icon" aria-hidden="true">🔒</span>
      This questionnaire has submissions and cannot be edited.
      Duplicate it to create an editable copy.
    </p>

    <div
      v-if="editable"
      class="edit-banner"
      role="status"
    >
      <svg class="edit-banner-icon" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
        <path d="M11.2 2.4l2.4 2.4L5.6 12.8H3.2v-2.4L11.2 2.4z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
      </svg>
      <span><strong>Edit mode</strong> · Changes save automatically as you work.</span>
    </div>

    <div
      v-if="composition.sections > 0"
      class="composition-strip"
    >{{ compositionText }}</div>

    <div v-if="editable" class="toolbar">
      <button
        type="button"
        class="btn btn-primary new-button"
        @click="onAddSectionClick"
      >
        <svg class="plus-icon" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        Add section
      </button>
    </div>

    <p
      v-if="sortedSections.length === 0 && (!editable || openForm.kind !== 'add-section')"
      class="empty-state"
    >
      No sections yet.<template v-if="locked"> This questionnaire is locked.</template><template v-else-if="editable"> Add a section to get started.</template><template v-else> Click <strong>Edit</strong> to add some.</template>
    </p>

    <p
      v-else-if="isFilterEmpty"
      class="empty-state"
    >
      No incomplete cards in this filter.
    </p>

    <!-- Drag mode (no translation filter).
         Sections are per-questionnaire and carry seq_card_sections.position.
         onSectionsReordered writes the new order through
         reorderSectionsInQuestionnaire (which rewrites position); the /items
         endpoint orders by that position, so the order survives a refetch.
         Enabled in edit mode only. -->
    <VueDraggable
      v-if="sortedSections.length > 0 && !filterMissingLocale"
      v-model="localSections"
      class="sections"
      :animation="180"
      handle=".section-drag-handle"
      :disabled="!editable"
      ghost-class="section-drag-ghost"
      item-key="id"
      @end="onSectionsReordered"
    >
      <QuestionnaireSectionRow
        v-for="section in localSections"
        :key="section.id"
        :section="section"
        :items="itemsBySection[section.id] || []"
        :read-only="!editable"
        :locked="locked"
        :edit-mode="editable"
        :is-adding-card="openForm.kind === 'add-card' && openForm.sectionId === section.id"
        :is-submitting-card="submittingCard && openForm.kind === 'add-card' && openForm.sectionId === section.id"
        :card-server-error="openForm.kind === 'add-card' && openForm.sectionId === section.id ? cardServerError : null"
        @editSection="onEditSection"
        @deleteSection="onDeleteSectionClick"
        @editCard="onEditCard"
        @deleteCard="onDeleteCardClick"
        @requestAddCard="onRequestAddCard"
        @submitAddCard="onSubmitAddCard"
        @cancelAddCard="onCancelInlineForm"
        @clearCardServerError="cardServerError = null"
        @cardsReordered="onCardsReordered"
      />
    </VueDraggable>

    <!-- Filter mode. Drag is suppressed because reordering a filtered
         subset would mislead — saving a new order would skip the hidden
         items. Plain list, same row component. -->
    <div
      v-else-if="sortedSections.length > 0 && filterMissingLocale && !isFilterEmpty"
      class="sections"
    >
      <QuestionnaireSectionRow
        v-for="section in visibleSections"
        :key="section.id"
        :section="section"
        :items="filteredItemsBySection[section.id] || []"
        :read-only="!editable"
        :locked="locked"
        :edit-mode="editable"
        :is-adding-card="openForm.kind === 'add-card' && openForm.sectionId === section.id"
        :is-submitting-card="submittingCard && openForm.kind === 'add-card' && openForm.sectionId === section.id"
        :card-server-error="openForm.kind === 'add-card' && openForm.sectionId === section.id ? cardServerError : null"
        @editSection="onEditSection"
        @deleteSection="onDeleteSectionClick"
        @editCard="onEditCard"
        @deleteCard="onDeleteCardClick"
        @requestAddCard="onRequestAddCard"
        @submitAddCard="onSubmitAddCard"
        @cancelAddCard="onCancelInlineForm"
        @clearCardServerError="cardServerError = null"
        @cardsReordered="onCardsReordered"
      />
    </div>

    <div v-if="editable" class="footer-form">
      <button
        v-if="openForm.kind !== 'add-section'"
        type="button"
        class="add-inline-button add-section-button"
        @click="onAddSectionClick"
      >
        <svg class="plus-icon" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        Add section
      </button>

      <form
        v-else
        class="inline-form"
        @submit.prevent="onSubmitAddSection"
      >
        <div class="inline-form-fields">
          <input
            ref="sectionNameInput"
            v-model="newSectionName"
            type="text"
            class="inline-input inline-input-text"
            placeholder="Section name (Deutsch)"
            autocomplete="off"
            spellcheck="false"
            :disabled="submittingSection"
            @input="onSectionNameInput"
          >
          <input
            v-model="newSectionSlug"
            type="text"
            class="inline-input inline-input-mono"
            placeholder="section-slug"
            autocomplete="off"
            autocapitalize="off"
            spellcheck="false"
            :disabled="submittingSection"
            @input="onSectionSlugInput"
          >
          <div class="inline-form-actions">
            <button
              type="button"
              class="btn btn-secondary btn-sm"
              :disabled="submittingSection"
              @click="onCancelInlineForm"
            >Cancel</button>
            <button
              type="submit"
              class="btn btn-primary btn-sm"
              :disabled="!isSectionFormValid || submittingSection"
            >{{ submittingSection ? 'Saving…' : 'Save' }}</button>
          </div>
        </div>
        <p v-if="sectionFormError" class="inline-form-error">{{ sectionFormError }}</p>
      </form>
    </div>

    <!-- Section editor (Phase 2) -->
    <SectionEditorModal
      v-if="editingSection"
      :section="editingSection"
      :mode="editable ? 'edit' : 'view'"
      :submitting="editingSectionSubmitting"
      :server-error="editingSectionError"
      @submit="onSectionEditorSubmit"
      @cancel="onSectionEditorCancel"
    />

    <!-- Add-card chooser. Junction Step 3: selecting a row POSTs to
         /items and appends to local state; modal closes on success.
         Exclude lists are informational (mark already-present rows with
         a badge) — duplicates are still allowed. -->
    <AddCardModal
      v-if="addCardTargetSection"
      :questionnaire-slug="questionnaireSlug"
      :section-id="addCardTargetSection.id"
      :section-name="pickName(addCardTargetSection.name, addCardTargetSection.slug)"
      :exclude-question-ids="excludeQuestionIdsForModal"
      :exclude-card-ids="excludeContentCardIdsForModal"
      @close="onAddCardClose"
      @select-question="onAddCardSelectQuestion"
      @select-card="onAddCardSelectCard"
      @select-flow="onAddCardSelectFlow"
    />

    <!-- "Add section" opens straight on the new-section form (create mode),
         skipping the reuse list. The created section drops into the local
         empty-overlay so it shows immediately, ready to receive items via
         the AddCardModal. -->
    <SectionPickerPopup
      v-if="showSectionPicker"
      initial-mode="create"
      :questionnaire-slug="questionnaireSlug"
      @select-section="onSectionPickerSelect"
      @close="showSectionPicker = false"
    />

    <!-- Card editor placeholder (Phase 2 — card editor is the next iteration) -->
    <div
      v-if="placeholderModal"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="placeholder-title"
      @click.self="placeholderModal = null"
    >
      <div class="modal modal-confirm">
        <h2 id="placeholder-title" class="modal-title">{{ placeholderModal.title }}</h2>
        <p class="modal-text">{{ placeholderModal.body }}</p>
        <div class="modal-footer">
          <span></span>
          <button
            type="button"
            class="btn btn-secondary"
            @click="placeholderModal = null"
          >Close</button>
        </div>
      </div>
    </div>

    <!-- Section delete confirm -->
    <div
      v-if="deleteSectionTarget"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="del-section-title"
      @click.self="onDeleteSectionCancel"
    >
      <div class="modal modal-confirm">
        <h2 id="del-section-title" class="modal-title">
          Delete section "{{ pickName(deleteSectionTarget.name, deleteSectionTarget.slug) }}"?
        </h2>
        <p class="modal-text">
          <template v-if="deleteSectionCardCount === 0">
            This section is empty. Deleting it cannot be undone.
          </template>
          <template v-else>
            This section has
            <strong>{{ deleteSectionCardCount }}</strong>
            {{ deleteSectionCardCount === 1 ? 'card' : 'cards' }}.
            Delete or move them before deleting the section.
          </template>
        </p>

        <p
          v-if="deleteSectionError"
          class="modal-error"
          role="alert"
        >{{ deleteSectionError }}</p>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="deletingSection"
            @click="onDeleteSectionCancel"
          >Cancel</button>
          <button
            type="button"
            class="btn btn-danger"
            :disabled="deleteSectionCardCount > 0 || deletingSection"
            @click="onConfirmDeleteSection"
          >{{ deletingSection ? 'Deleting…' : 'Delete section' }}</button>
        </div>
      </div>
    </div>

    <!-- Item-remove confirm. Junction Step 3: deletes only the junction
         row; the source row in the bank / content library is untouched.
         Wording reassures the user that the source survives. -->
    <div
      v-if="deleteCardTarget"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="del-card-title"
      @click.self="onDeleteCardCancel"
    >
      <div class="modal modal-confirm">
        <h2 id="del-card-title" class="modal-title">
          Remove this item from the questionnaire?
        </h2>
        <p class="modal-text">
          The source {{ removeTargetSourceLabel }}
          stays in the {{ removeTargetLibraryLabel }}
          and can be added again later.
        </p>

        <p
          v-if="deleteCardError"
          class="modal-error"
          role="alert"
        >{{ deleteCardError }}</p>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="deletingCard"
            @click="onDeleteCardCancel"
          >Cancel</button>
          <button
            type="button"
            class="btn btn-danger"
            :disabled="deletingCard"
            @click="onConfirmDeleteCard"
          >{{ deletingCard ? 'Removing…' : 'Remove' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { VueDraggable } from 'vue-draggable-plus';
import apiService from '@/services/apiService';
import QuestionnaireSectionRow from './QuestionnaireSectionRow.vue';
import SectionEditorModal from './SectionEditorModal.vue';
import AddCardModal from './AddCardModal.vue';
import SectionPickerPopup from './SectionPickerPopup.vue';
import {
  cardsState,
  sectionsForQuestionnaire,
  cardsForSection,
  cardCountForSection,
  cardTypeCountsForQuestionnaire,
  itemsForSection,
  appendItemToCardsState,
  removeItemFromCardsState,
  reorderItemsLocally,
  revertItemsOrderLocally,
  pickName,
  suggestSectionSlug,
  isSectionSlugTaken,
  removeSection,
  removeCard,
  reorderSections,
  reorderCardsInSection,
  updateSectionName,
  SLUG_REGEX,
} from './_data/cards.js';
import {
  itemHasMissingTranslations,
  sectionNameHasMissingTranslation,
} from '@/utils/translationCompleteness.js';

export default {
  name: 'QuestionnaireSectionList',
  components: { VueDraggable, QuestionnaireSectionRow, SectionEditorModal, AddCardModal, SectionPickerPopup },
  props: {
    questionnaireSlug: { type: String, required: true },
    locked: { type: Boolean, default: false },
    editMode: { type: Boolean, default: false },
    // When non-null, filter the visible cards to those that have at least
    // one in-use translatable field empty in this locale, and hide sections
    // that contain no such cards (and whose own name isn't missing in that
    // locale either). Section drag-and-drop is disabled while the filter
    // is active, since reordering a filtered subset would be confusing.
    filterMissingLocale: { type: String, default: null },
  },
  data() {
    return {
      // openForm tracks which inline mini-form is currently open. Only one
      // can be open at a time across the whole page; opening a new one
      // closes the previous (and clears the inline state in the row that
      // was open).
      openForm: { kind: null, sectionId: null },

      // Mirror of sortedSections used as the v-model for VueDraggable. We
      // keep a local copy so vue-draggable-plus can mutate it during drag,
      // then sync back to the store on @end.
      localSections: [],

      newSectionName: '',
      newSectionSlug: '',
      lastSuggestedSlug: '',
      // POST in flight. Disables the Save button and swaps its label.
      submittingSection: false,
      // Server-side error after a failed POST (409 collision, 404,
      // network failure, etc.). Displayed in the same slot as the
      // client-side validation error; cleared on input change so the
      // user sees their typing isn't broken.
      sectionServerError: null,
      // DELETE in flight + error to surface inside the confirm modal.
      deletingSection: false,
      deleteSectionError: null,
      // Card POST in flight + server error to pipe back into the
      // SectionRow that owns the open Add-card form. Only one form is
      // open at a time so a single pair of refs covers it.
      submittingCard: false,
      cardServerError: null,
      // DELETE in flight + error to surface inside the card-delete
      // confirm modal.
      deletingCard: false,
      deleteCardError: null,

      placeholderModal: null,   // { title, body } | null — used by the card editor placeholder
      // Add-card chooser. Step 1: opens the new modal in place of the
      // inline form. The inline form template still exists in
      // QuestionnaireSectionRow but is unreachable from this list now
      // (we no longer set openForm.kind = 'add-card'). It will be
      // removed in a later step once the new flow is fully wired.
      addCardTargetSection: null,  // section object | null
      // Section picker (Step 60 of the junction work). Opens via the
      // "+ Add section" toolbar button instead of the legacy inline
      // form. The inline-form template is left in place but unreachable
      // because openForm.kind is no longer set to 'add-section'.
      showSectionPicker: false,
      // Local overlay: sections the user picked from the popup that
      // don't yet have any items in this questionnaire. Each entry has
      // {id, slug, name} (same shape the popup emits). Cleared when the
      // page is unmounted; section *visibility* persists naturally once
      // an item lands in the junction because the items-derived list
      // picks it up. This list is the bridge between "picked" and
      // "in the database".
      pickedSectionsLocal: [],
      editingSection: null,     // section object currently in the editor modal | null
      // PATCH in flight + server-side error to surface in the modal.
      // Modal stays open on error so the user can fix and retry.
      editingSectionSubmitting: false,
      editingSectionError: null,
      deleteSectionTarget: null, // section object | null
      deleteCardTarget: null,    // card object | null
    };
  },
  computed: {
    /**
     * Sections to render in the questionnaire structure. Post-junction-
     * Step-3, sections are global: a questionnaire's section list is
     * derived from the distinct `section_id`s on its items, NOT from a
     * per-questionnaire seq_card_sections fetch. To this we add the
     * local `pickedSectionsLocal` overlay so a freshly-picked section
     * shows up as an empty group until the user adds the first item.
     *
     * Sort: alphabetical by name.en with fallback to slug — matches the
     * popup's order, so picking a section and seeing it in the structure
     * doesn't reshuffle the rest.
     */
    sortedSections() {
      // eslint-disable-next-line no-unused-vars
      const _len = cardsState.items.length;
      const seen = new Map();
      // Pass 1: pull section info from the items themselves. The /items
      // endpoint joins seq_card_sections so each item carries its
      // section_name + section_slug already.
      for (const it of cardsState.items) {
        if (it.questionnaire_slug !== this.questionnaireSlug) continue;
        if (it.section_id == null) continue;
        if (!seen.has(it.section_id)) {
          seen.set(it.section_id, {
            id: it.section_id,
            slug: it.section_slug || `section_${it.section_id}`,
            name: it.section_name || {},
          });
        }
      }
      // Pass 2: empty-overlay. Pick-but-no-items-yet sections.
      for (const s of this.pickedSectionsLocal) {
        if (!seen.has(s.id)) seen.set(s.id, s);
      }
      // Render in the order the /items endpoint returned them — it orders
      // sections by seq_card_sections.position. No alphabetical re-sort
      // here, or it would fight the server order and a drag-reorder would
      // snap back on refetch.
      return Array.from(seen.values());
    },
    // Pre-grouped items per section so each section row gets its slice
    // without re-iterating cardsState.items on every render. Junction
    // Step 2: reads from cardsState.items (filled by /items endpoint),
    // not cardsState.cards.
    itemsBySection() {
      // eslint-disable-next-line no-unused-vars
      const _len = cardsState.items.length;
      const out = {};
      for (const s of this.sortedSections) {
        out[s.id] = itemsForSection(this.questionnaireSlug, s.id);
      }
      return out;
    },
    deleteSectionCardCount() {
      // Used by the delete-section confirm to warn when the section has
      // children. Counts the items in this section post-cutover.
      if (!this.deleteSectionTarget) return 0;
      return (this.itemsBySection[this.deleteSectionTarget.id] || []).length;
    },
    /**
     * Question source ids already present in the target section of the
     * Add Card modal. Forwarded to the question chooser as an
     * informational signal ("Already in this section" badge) — clicks
     * still work because Step 3 allows duplicates.
     */
    excludeQuestionIdsForModal() {
      if (!this.addCardTargetSection) return [];
      const items = this.itemsBySection[this.addCardTargetSection.id] || [];
      return items
        .filter(it => it.item_type === 'question' && it.item)
        .map(it => it.item.id);
    },
    /**
     * Content card source ids already present in the target section.
     * Same informational-only semantics as excludeQuestionIdsForModal.
     */
    excludeContentCardIdsForModal() {
      if (!this.addCardTargetSection) return [];
      const items = this.itemsBySection[this.addCardTargetSection.id] || [];
      return items
        .filter(it => it.item_type === 'content_card' && it.item)
        .map(it => it.item.id);
    },
    /**
     * Human label for the source kind in the remove-confirm dialog:
     * "question" / "content card" / "flow card". Used in the
     * "The source <X> stays in the <Y>" sentence.
     */
    removeTargetSourceLabel() {
      const it = this.deleteCardTarget;
      if (!it) return 'item';
      if (it.item_type === 'question') return 'question';
      if (it.item_type === 'content_card') return 'content card';
      if (it.item_type === 'flow_card') return 'flow card';
      return 'item';
    },
    /**
     * Human label for the source library in the remove-confirm dialog:
     * "question bank" / "content library" / "flow catalog".
     */
    removeTargetLibraryLabel() {
      const it = this.deleteCardTarget;
      if (!it) return 'library';
      if (it.item_type === 'question') return 'question bank';
      if (it.item_type === 'content_card') return 'content library';
      if (it.item_type === 'flow_card') return 'flow catalog';
      return 'library';
    },
    /**
     * Items keyed by section id, with the translation filter applied.
     * When `filterMissingLocale` is null this returns the same data as
     * `itemsBySection`. When it's set, each section's items are reduced
     * to those that have at least one in-use field missing in that locale.
     */
    filteredItemsBySection() {
      if (!this.filterMissingLocale) return this.itemsBySection;
      const loc = this.filterMissingLocale;
      const out = {};
      for (const s of this.localSections) {
        const all = this.itemsBySection[s.id] || [];
        out[s.id] = all.filter(it => itemHasMissingTranslations(it, loc));
      }
      return out;
    },
    /**
     * Sections to render, filtered by `filterMissingLocale`. A section is
     * kept iff its own name has a missing translation in that locale OR
     * it contains at least one matching item. When filter is null, this
     * is just `localSections` — empty sections stay visible so the user
     * can still see the structure of the questionnaire (left-join
     * render, not item-fold).
     */
    visibleSections() {
      if (!this.filterMissingLocale) return this.localSections;
      const loc = this.filterMissingLocale;
      return this.localSections.filter(s => {
        if (sectionNameHasMissingTranslation(s, loc)) return true;
        const items = this.filteredItemsBySection[s.id] || [];
        return items.length > 0;
      });
    },
    /**
     * True only when the filter is active and the filter has hidden every
     * section. With a non-100% locale this should be empty in practice,
     * but we render a tidy empty state for safety.
     */
    isFilterEmpty() {
      return (
        !!this.filterMissingLocale
        && this.sortedSections.length > 0
        && this.visibleSections.length === 0
      );
    },
    // The ONLY editing gate that matters at runtime: editMode AND not
    // locked. The toggle in the parent is disabled when locked, so this
    // belt-and-braces is mostly defensive — but it means children can
    // ignore the lock and just look at editable.
    editable() {
      return this.editMode && !this.locked;
    },
    // Composition counts: questions / content cards / flow cards / sections,
    // always all four even when zero. Drives the muted summary strip just
    // above the section list. Junction Step 2: counts by item_type on the
    // items array, not by card_type on the legacy seq_cards rows.
    composition() {
      // eslint-disable-next-line no-unused-vars
      const _ls = cardsState.sections.length;
      // eslint-disable-next-line no-unused-vars
      const _li = cardsState.items.length;
      const counts = { question: 0, content_card: 0, flow_card: 0, sections: 0 };
      for (const it of cardsState.items) {
        if (it.questionnaire_slug !== this.questionnaireSlug) continue;
        if (counts[it.item_type] !== undefined) counts[it.item_type]++;
      }
      counts.sections = this.sortedSections.length;
      return counts;
    },
    compositionText() {
      const c = this.composition;
      return [
        `${c.question} ${c.question === 1 ? 'question' : 'questions'}`,
        `${c.content_card} ${c.content_card === 1 ? 'content card' : 'content cards'}`,
        `${c.flow_card} ${c.flow_card === 1 ? 'flow card' : 'flow cards'}`,
        `${c.sections} ${c.sections === 1 ? 'section' : 'sections'}`,
      ].join(' · ');
    },
    isSectionFormValid() {
      const name = (this.newSectionName || '').trim();
      const slug = (this.newSectionSlug || '').trim();
      if (!name) return false;
      if (!slug) return false;
      if (!SLUG_REGEX.test(slug)) return false;
      if (isSectionSlugTaken(this.questionnaireSlug, slug)) return false;
      return true;
    },
    sectionFormError() {
      // Server errors take priority — they're a hard rejection and the
      // user needs to see them even if the client validation now thinks
      // everything is fine (e.g. they slightly tweaked the slug after a
      // 409 came back, but it still collides somewhere we don't track).
      if (this.sectionServerError) return this.sectionServerError;
      const name = (this.newSectionName || '').trim();
      const slug = (this.newSectionSlug || '').trim();
      if (!name) return null;
      if (!slug) return null;
      if (!SLUG_REGEX.test(slug)) {
        return 'Slug must be lowercase letters, digits, hyphens or underscores. 2–64 chars, starts with a letter.';
      }
      if (isSectionSlugTaken(this.questionnaireSlug, slug)) {
        return `Slug "${slug}" is already used by another section in this questionnaire.`;
      }
      return null;
    },
  },
  watch: {
    sortedSections: {
      immediate: true,
      handler(next) {
        this.localSections = next.slice();
      },
    },
  },
  methods: {
    pickName,

    // ---------- single-open-form coordinator ----------
    closeAllForms() {
      this.openForm = { kind: null, sectionId: null };
      this.newSectionName = '';
      this.newSectionSlug = '';
      this.lastSuggestedSlug = '';
      this.sectionServerError = null;
      this.cardServerError = null;
    },

    onAddSectionClick() {
      if (!this.editable) return;
      // Junction Step 3 (Step 60): open the SectionPickerPopup instead
      // of toggling the inline form. The inline form template stays in
      // place but is unreachable — `openForm.kind` is no longer set to
      // 'add-section' anywhere. Removed in a later cleanup pass.
      this.showSectionPicker = true;
    },

    /**
     * Handler for the section picker's select-section event.
     *
     * Adds the picked section to the local "picked-but-empty" overlay
     * so it shows up as an empty section group in the questionnaire's
     * structure immediately. The user can then add items via the
     * AddCardModal (which sets that section_id on the new junction
     * row). Once at least one item lands in the section, the
     * items-derived branch of sortedSections picks it up and the
     * overlay entry becomes redundant — but harmless; the Map de-dupes.
     */
    onSectionPickerSelect(section) {
      const idx = this.pickedSectionsLocal.findIndex(s => s.id === section.id);
      if (idx === -1) {
        this.pickedSectionsLocal.push({
          id: section.id,
          slug: section.slug,
          name: section.name || {},
        });
      } else {
        // Refresh the snapshot in case the popup edited the name.
        this.pickedSectionsLocal.splice(idx, 1, {
          id: section.id,
          slug: section.slug,
          name: section.name || {},
        });
      }
      this.showSectionPicker = false;
    },

    onCancelInlineForm() {
      this.closeAllForms();
    },

    onSectionNameInput() {
      // Auto-suggest the slug from the name unless the user has typed a
      // custom slug. We compare against the last suggestion we filled in
      // — if it still matches, the user hasn't customised it.
      if (this.newSectionSlug === '' || this.newSectionSlug === this.lastSuggestedSlug) {
        const next = suggestSectionSlug(this.newSectionName);
        this.newSectionSlug = next;
        this.lastSuggestedSlug = next;
      }
      // Clear server error so the user's typing isn't shadowed by a
      // stale failure message.
      if (this.sectionServerError) this.sectionServerError = null;
    },

    onSectionSlugInput() {
      if (this.sectionServerError) this.sectionServerError = null;
    },

    async onSubmitAddSection() {
      if (!this.isSectionFormValid) return;
      if (this.submittingSection) return;
      const name = this.newSectionName.trim();
      const slug = this.newSectionSlug.trim();
      this.submittingSection = true;
      this.sectionServerError = null;
      try {
        // POST /api/seq/questionnaires/:slug/sections
        // Body: { slug, name: {de}, position? } — position omitted so
        // the backend appends.
        const created = await apiService.createSectionForQuestionnaire(
          this.questionnaireSlug,
          { slug, name: { de: name } },
        );
        // Push the server row into the local store, stamped with the
        // questionnaire slug (the API response doesn't include that
        // denormalised field).
        cardsState.sections.push({
          ...created,
          questionnaire_slug: this.questionnaireSlug,
        });
        // eslint-disable-next-line no-console
        console.log('[API] Section created:', created);
        this.closeAllForms();
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 409) {
          this.sectionServerError = `Slug "${slug}" is already used by another section in this questionnaire.`;
        } else if (status === 404) {
          this.sectionServerError = 'This questionnaire could not be found. Reload the page and try again.';
        } else if (Array.isArray(detail)) {
          this.sectionServerError = detail.map(d => d.msg).join(' ');
        } else if (typeof detail === 'string') {
          this.sectionServerError = detail;
        } else {
          this.sectionServerError = 'Failed to create section. Please try again.';
        }
      } finally {
        this.submittingSection = false;
      }
    },

    // ---------- section interactions ----------

    onEditSection(section) {
      // Phase 2 modal. Mode toggles based on editable: editor pages
      // get the editable form, locked questionnaires (or view mode)
      // get the read-only variant. The same modal serves both so the
      // user always has somewhere to inspect a section's translations.
      this.editingSection = section;
      this.editingSectionError = null;
    },

    onSectionEditorCancel() {
      // Don't bail mid-request — the user might think they cancelled
      // when the PATCH actually succeeded.
      if (this.editingSectionSubmitting) return;
      this.editingSection = null;
      this.editingSectionError = null;
    },

    async onSectionEditorSubmit({ name }) {
      if (!this.editingSection) return;
      if (this.editingSectionSubmitting) return;
      const sectionId = this.editingSection.id;
      this.editingSectionSubmitting = true;
      this.editingSectionError = null;
      try {
        // PATCH /api/seq/sections/:id
        // Body: { name: <new JSONB> }
        // Response: full updated row.
        const updated = await apiService.updateSection(sectionId, { name });
        // Take the server's name (in case the backend ever normalises)
        // rather than the form payload. updated_at would also be fresh
        // here if the UI ever wants to surface it.
        updateSectionName(sectionId, updated.name);
        // eslint-disable-next-line no-console
        console.log('[API] Section updated', sectionId, '→', updated.name);
        this.editingSection = null;
        this.$emit('show-success', 'Section updated.');
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 404) {
          this.editingSectionError = 'This section no longer exists. Close this dialog and reload the page.';
        } else if (Array.isArray(detail)) {
          this.editingSectionError = detail.map(d => d.msg).join(' ');
        } else if (typeof detail === 'string') {
          this.editingSectionError = detail;
        } else {
          this.editingSectionError = 'Failed to save section. Please try again.';
        }
      } finally {
        this.editingSectionSubmitting = false;
      }
    },

    onDeleteSectionClick(section) {
      if (!this.editable) return;
      this.deleteSectionError = null;
      this.deleteSectionTarget = section;
    },

    onDeleteSectionCancel() {
      if (this.deletingSection) return; // Don't bail mid-request.
      this.deleteSectionTarget = null;
      this.deleteSectionError = null;
    },

    async onConfirmDeleteSection() {
      if (!this.deleteSectionTarget) return;
      if (this.deleteSectionCardCount > 0) return; // Defence-in-depth; the
      // button is also disabled. UX rule is stricter than the schema:
      // seq_cards.section_id is ON DELETE SET NULL, so the database would
      // happily orphan the cards. The backend mirrors this rule with a
      // 409 response containing the live card count.
      if (this.deletingSection) return;
      const id = this.deleteSectionTarget.id;
      this.deletingSection = true;
      this.deleteSectionError = null;
      try {
        await apiService.deleteSection(id);
        removeSection(id);
        // eslint-disable-next-line no-console
        console.log('[API] Section deleted', id);
        this.deleteSectionTarget = null;
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 404) {
          this.deleteSectionError = 'This section no longer exists. It may have been deleted by someone else. Close this dialog and reload the page.';
        } else if (status === 409) {
          // Backend returns the authoritative card count in the message;
          // prefer that over our local count which could be stale.
          this.deleteSectionError = typeof detail === 'string'
            ? detail
            : 'This section has cards. Empty it before deleting.';
        } else if (Array.isArray(detail)) {
          this.deleteSectionError = detail.map(d => d.msg).join(' ');
        } else if (typeof detail === 'string') {
          this.deleteSectionError = detail;
        } else {
          this.deleteSectionError = 'Failed to delete section. Please try again.';
        }
      } finally {
        this.deletingSection = false;
      }
    },

    async onSectionsReordered() {
      // Optimistic-with-revert, same shape as onCardsReordered.
      const orderedIds = this.localSections.map(s => s.id);
      const prevIds = sectionsForQuestionnaire(this.questionnaireSlug).map(s => s.id);
      reorderSections(orderedIds);
      try {
        await apiService.reorderSectionsInQuestionnaire(this.questionnaireSlug, orderedIds);
        // eslint-disable-next-line no-console
        console.log('[API] Sections reordered →', orderedIds);
      } catch (e) {
        reorderSections(prevIds);
        let msg = 'Failed to save section order. Reverted to the previous order.';
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 404) {
          msg = 'This questionnaire no longer exists. Your reorder was reverted — reload the page to see the current state.';
        } else if (status === 422 && detail && typeof detail === 'object') {
          // detail = { message, missing: [...], extra: [...] }
          msg = 'Sections changed in this questionnaire while you were dragging. Reverted — reload to see the current state.';
        } else if (typeof detail === 'string') {
          msg = detail;
        }
        this.$emit('show-error', msg);
      }
    },

    // ---------- card interactions ----------

    onEditCard(card) {
      this.placeholderModal = {
        title: `Card: ${card.code}`,
        body: 'Card editor coming soon. In Phase 2 you will be able to edit the prompt, answer options, tooltip, and translations for question cards (and the equivalent fields for the other card types).',
      };
    },

    onRequestAddCard(section) {
      if (!this.editable) return;
      this.cardServerError = null;
      // Step 1: open the AddCardModal chooser instead of toggling the
      // inline form. The inline form template still lives in
      // QuestionnaireSectionRow, but isAddingCard never goes true now,
      // so it's effectively dead — to be removed in a later step.
      this.addCardTargetSection = section;
    },

    onAddCardClose() {
      this.addCardTargetSection = null;
    },

    onAddCardSelectQuestion(question) {
      // Junction Step 3: append a junction row referencing this
      // question. Questions are gold — the bank row is untouched.
      this._addItemToTargetSection({
        item_type: 'question',
        item_id: question.id,
        label: question.code,
      });
    },

    onAddCardSelectCard(card) {
      // Same path as questions for content cards. Step 3 unifies the
      // write through one helper; the modal sends `select-card` for the
      // content-card tab.
      this._addItemToTargetSection({
        item_type: 'content_card',
        item_id: card.id,
        label: `#${card.id}`,
      });
    },

    onAddCardSelectFlow(flowCard) {
      // Flow cards reference the global seq_flow_cards catalogue by id —
      // same write path as questions and content cards.
      this._addItemToTargetSection({
        item_type: 'flow_card',
        item_id: flowCard.id,
        label: flowCard.code,
      });
    },

    /**
     * Shared write path for the two chooser tabs. POSTs to the items
     * endpoint, appends the resolved row to local state, closes the
     * modal on success. 404 means the questionnaire or section was
     * deleted under us — close + refetch so the page is honest.
     */
    async _addItemToTargetSection({ item_type, item_id, label }) {
      const target = this.addCardTargetSection;
      if (!target) return;
      try {
        const newItem = await apiService.addItemToQuestionnaire(
          this.questionnaireSlug,
          {
            section_id: target.id,
            item_type,
            item_id,
          },
        );
        appendItemToCardsState(this.questionnaireSlug, newItem);
        this.addCardTargetSection = null;
        this.$emit('show-success', `Added: ${label}`);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 404) {
          // Stale page — the questionnaire or section vanished. Drop
          // the modal and trigger a refetch via the parent so the user
          // doesn't see a now-impossible state.
          this.addCardTargetSection = null;
          this.$emit('show-error', 'This questionnaire is no longer available — refreshing.');
          this.$emit('refetch-content');
          return;
        }
        const detail = e?.response?.data?.detail;
        const msg = typeof detail === 'string'
          ? detail
          : 'Could not add the item. Please try again.';
        this.$emit('show-error', msg);
      }
    },

    async onSubmitAddCard({ section, card_type, code, input_style }) {
      if (this.submittingCard) return;
      // Build the content blob client-side so the API stays generic.
      // input_style is a question-only convention; for other types we
      // send an empty object and the renderer's per-type logic handles
      // the rest (Phase 2: actual editor for content).
      const content = {};
      if (card_type === 'question' && input_style) {
        content.input_style = input_style;
      }
      this.submittingCard = true;
      this.cardServerError = null;
      try {
        const created = await apiService.createCardInSection(section.id, {
          card_type,
          code,
          content,
        });
        // The API returns the canonical row (backend-assigned id, position,
        // is_required, timestamps). Stamp the slug for the local store's
        // denormalised filter and push.
        cardsState.cards.push({
          ...created,
          questionnaire_slug: section.questionnaire_slug,
        });
        // eslint-disable-next-line no-console
        console.log('[API] Card created:', created);
        this.closeAllForms();
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 409) {
          this.cardServerError = `Code "${code}" is already used by another card in this questionnaire.`;
        } else if (status === 404) {
          this.cardServerError = 'This section no longer exists. Reload the page and try again.';
        } else if (Array.isArray(detail)) {
          this.cardServerError = detail.map(d => d.msg).join(' ');
        } else if (typeof detail === 'string') {
          this.cardServerError = detail;
        } else {
          this.cardServerError = 'Failed to create card. Please try again.';
        }
      } finally {
        this.submittingCard = false;
      }
    },

    onDeleteCardClick(card) {
      if (!this.editable) return;
      this.deleteCardError = null;
      this.deleteCardTarget = card;
    },

    onDeleteCardCancel() {
      if (this.deletingCard) return; // Don't bail mid-request.
      this.deleteCardTarget = null;
      this.deleteCardError = null;
    },

    async onConfirmDeleteCard() {
      // Junction Step 3: delete one junction row via the items endpoint.
      // The source row in the bank / content library / flow catalog is
      // untouched (gold-source principle, enforced by ON DELETE RESTRICT
      // on the source FKs).
      if (!this.deleteCardTarget) return;
      if (this.deletingCard) return;
      const id = this.deleteCardTarget.id;
      this.deletingCard = true;
      this.deleteCardError = null;
      try {
        await apiService.removeItemFromQuestionnaire(
          this.questionnaireSlug, id,
        );
        removeItemFromCardsState(id);
        this.deleteCardTarget = null;
        this.$emit('show-success', 'Removed.');
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 404) {
          this.deleteCardError = 'This item no longer exists. Close this dialog and reload to see the current state.';
        } else if (Array.isArray(detail)) {
          this.deleteCardError = detail.map(d => d.msg).join(' ');
        } else if (typeof detail === 'string') {
          this.deleteCardError = detail;
        } else {
          this.deleteCardError = 'Failed to remove item. Please try again.';
        }
      } finally {
        this.deletingCard = false;
      }
    },

    async onCardsReordered({ section, orderedIds }) {
      // Junction Step 3: the SectionRow's VueDraggable emits this event
      // with the new junction-row id ordering for that section. Apply
      // locally first (so the drag visually sticks), then PATCH the
      // items/order endpoint. On any failure revert via the stored
      // snapshot — the SectionRow's localItems watcher re-syncs from
      // the prop change automatically.
      const snapshot = reorderItemsLocally(
        this.questionnaireSlug, section.id, orderedIds,
      );
      try {
        await apiService.reorderItemsInSection(
          this.questionnaireSlug, section.id, orderedIds,
        );
      } catch (e) {
        revertItemsOrderLocally(snapshot);
        let msg = 'Failed to save item order. Reverted to the previous order.';
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 404) {
          msg = 'This questionnaire is no longer available — reload to see the current state.';
        } else if (status === 422 && detail && typeof detail === 'object') {
          // {message, missing, extra} — same shape as the legacy cards
          // reorder, so the wording is consistent.
          msg = 'Items changed in this section while you were dragging. Reverted — reload to see the current state.';
        } else if (typeof detail === 'string') {
          msg = detail;
        }
        this.$emit('show-error', msg);
      }
    },
  },
};
</script>

<style scoped>
.section-list-wrap {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.lock-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  padding: 12px 16px;
  background: var(--color-bg-soft);
  border: 0.5px solid var(--color-border);
  border-left: 3px solid var(--color-accent);
  border-radius: var(--radius-md);
  font-size: 13px;
  color: var(--color-text-secondary);
}

.lock-banner-icon {
  flex-shrink: 0;
  font-size: 14px;
  line-height: 1;
}

.toolbar {
  display: flex;
  justify-content: flex-end;
}

.composition-strip {
  padding: 12px 16px;
  background: var(--color-bg-soft);
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: 13px;
  color: var(--color-text-secondary);
  letter-spacing: 0.01em;
}

/* Edit-mode banner: sticky to the top of the cards-and-sections area
   while the user scrolls. Accent purple, white text, with a pencil
   icon on the left. Always visible during edit mode so the user knows
   where they are even after scrolling deep into a long questionnaire. */
.edit-banner {
  position: sticky;
  top: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--color-accent);
  color: white;
  padding: 11px 16px;
  border-radius: var(--radius-md);
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(83, 74, 183, 0.22);
}

.edit-banner-icon {
  flex-shrink: 0;
  color: white;
}

.btn {
  padding: 9px 16px;
  border-radius: var(--radius-md);
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  border: 0.5px solid var(--color-border);
  transition: opacity 0.15s, background 0.15s, border-color 0.15s;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.btn-primary {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-accent-dark);
  border-color: var(--color-accent-dark);
}

.btn-secondary {
  background: var(--color-bg-card);
  color: var(--color-text-primary);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--color-bg-soft);
  border-color: var(--color-border-strong);
}

.btn-danger {
  background: var(--color-bad);
  color: white;
  border-color: var(--color-bad);
}

.btn-danger:hover:not(:disabled) {
  background: var(--color-bad-dark);
  border-color: var(--color-bad-dark);
}

.new-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  white-space: nowrap;
}

.plus-icon {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
}

.empty-state {
  font-size: 14px;
  color: var(--color-text-tertiary);
  background: var(--color-bg-soft);
  border: 0.5px dashed var(--color-border);
  border-radius: var(--radius-md);
  padding: 28px 20px;
  text-align: center;
  margin: 0;
}

.sections {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section-drag-ghost {
  opacity: 0.4;
  background: var(--color-accent-soft);
}

.footer-form {
  display: flex;
}

.add-section-button {
  width: 100%;
  justify-content: center;
  padding: 12px 14px;
  font-size: 13px;
}

.add-inline-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 0.5px dashed var(--color-border);
  border-radius: var(--radius-md);
  padding: 7px 14px;
  font-family: inherit;
  font-size: 12px;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
}

.add-inline-button:hover {
  border-color: var(--color-accent);
  color: var(--color-accent);
  background: var(--color-bg-soft);
}

.inline-form {
  flex: 1;
  background: var(--color-bg-soft);
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 12px 14px;
}

.inline-form-fields {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.inline-input {
  font-family: inherit;
  font-size: 13px;
  padding: 7px 10px;
  border: 0.5px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-bg-card);
  color: var(--color-text-primary);
}

.inline-input-text {
  flex: 2;
  min-width: 160px;
}

.inline-input-mono {
  font-family: var(--font-mono);
  flex: 1;
  min-width: 140px;
}

.inline-input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 2px var(--color-accent-soft);
}

.inline-form-actions {
  display: flex;
  gap: 6px;
  margin-left: auto;
}

.inline-form-error {
  margin: 8px 0 0;
  font-size: 11px;
  color: #6B1F18;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  z-index: 2000;
}

.modal {
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1.5rem;
  max-width: 460px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.modal-title {
  font-size: 17px;
  font-weight: 600;
  margin: 0 0 8px;
  color: var(--color-text-primary);
}

.modal-text {
  font-size: 13.5px;
  color: var(--color-text-secondary);
  margin: 0 0 1.25rem;
  line-height: 1.55;
}

/* Server-error banner inside the section-delete confirm modal. Same
   palette as the matching style in AdminQuestionnaireDetail. */
.modal-error {
  font-size: 13px;
  color: #6B1F18;
  background: rgba(200, 57, 46, 0.10);
  border: 0.5px solid rgba(200, 57, 46, 0.45);
  border-radius: var(--radius-md);
  padding: 10px 14px;
  margin: 0 0 1.25rem;
  line-height: 1.5;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.modal-confirm .modal-footer .btn {
  padding: 9px 16px;
  font-size: 13px;
}
</style>
